import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import {
  Grid,
  Typography,
  TextField,
  FormControl,
  MenuItem,
  Divider,
  Select,
  makeStyles,
} from "@material-ui/core";
import { commonInlineStyles, inputFieldStyles } from "../../styles";
import ProductTypePdfConfigTable from "../../ProductTypePdfConfigTable";

const useStyles = makeStyles((theme) => ({
  // BCM-891: same table styling the other CMO sections use — a #f9f9fc header
  // band and 16px cells — with bold column headers.
  tableHead: {
    backgroundColor: "#f9f9fc",
  },
  tableHeadCell: {
    fontSize: 16,
    fontWeight: 700,
  },
  tableBodyCell: {
    fontSize: 16,
  },
  noPaddingMultiline: {
    "& .MuiOutlinedInput-root": {
      padding: "0 !important",
    },
    "& .MuiOutlinedInput-input": {
      padding: "8.5px 14px !important",
    },
  },
}));

const batchOptions = [
  "Yes, for batches with the same brand",
  "No, single batch only",
  "Yes, for batches with same and different brands",
];

const batchOptionToId = {
  "Yes, for batches with the same brand": 1,
  "No, single batch only": 2,
  "Yes, for batches with same and different brands": 3,
};

const idToBatchOption = {
  1: "Yes, for batches with the same brand",
  2: "No, single batch only",
  3: "Yes, for batches with same and different brands",
};

const ShipmentConfigSection = forwardRef(
  (
    {
      form: initialForm,
      errors: initialErrors,
      editMode: initialEditMode = false,
      onChange,
      vendorMosymphonyID,
      brandOwnerMosymphonyID,
      disableEdit = false,
      // BCM-891: rows for the Product Type Configurations table at the top of
      // this section. Owned by the parent, so vendorProductTypeConfigs keeps a
      // single writer at save time and this section stays presentational.
      productTypeConfigs = [],
      onProductTypeConfigChange,
    },
    ref
  ) => {
    const classes = useStyles();
    const [form, setForm] = useState({ ...initialForm });
    const [errors, setErrors] = useState({ ...initialErrors });
    const [editMode, setEditMode] = useState(initialEditMode);
    const [isModified, setIsModified] = useState(false);
    const [originalForm, setOriginalForm] = useState({ ...initialForm });

    // Update state when props change
    useEffect(() => {
      // Deep copy the initialForm to avoid reference issues
      const formCopy = { ...initialForm };

      setForm(formCopy);
      setOriginalForm({ ...formCopy });
      setErrors({ ...initialErrors });
      setEditMode(initialEditMode);
      setIsModified(false);
    }, [initialForm, initialErrors, initialEditMode]);

    // Check if form has been modified
    useEffect(() => {
      const hasChanged = JSON.stringify(form) !== JSON.stringify(originalForm);

      if (isModified !== hasChanged) {
        setIsModified(hasChanged);
        if (onChange) {
          onChange({ target: { name: "formChanged", value: hasChanged } });
        }
      }
    }, [form, originalForm, onChange, isModified]);
    // Expose methods to parent component
    useImperativeHandle(ref, () => ({
      getFormData: () => form,
      getPayload: () => {
        const shipmentDurationValue =
          form.shipmentDuration === "" ? "" : Number(form.shipmentDuration);

        return {
          vendorMosymphonyID,
          brandOwnerMosymphonyID,
          vendorPlantDetails: {
            // Map the batch option to the correct ID value
            cmoBatchShipmentSettingID: form.allowMultipleBatches
              ? batchOptionToId[form.allowMultipleBatches]
              : null,
            // This boolean is derived from the cmoBatchShipmentSettingID (anything other than 2 means "Yes")
            // allowMultipleBatches: form.allowMultipleBatches !== "No, single batch only",
            // Only convert to number when there's a value, otherwise send empty string
            shipmentDuration: shipmentDurationValue,
            automateGRP: form.automateGoodsReceiptProcess === "Yes",
            allocationOwnedBySponsor: form.allocationOwnedBySponsor === "Yes",
            batchAllocationPDFNotificationReqd:
              form.batchAllocationPdfRequired === "Yes",
            batchAllocationPDFNotificationSubject:
              form.batchAllocationPdfRequired === "Yes"
                ? form.pdfFieldSubject
                : null,
            batchAllocationPDFNotificationObjective:
              form.batchAllocationPdfRequired === "Yes"
                ? form.pdfFieldObjective
                : null,
            domPresentOnDigitizationUI:
              form.sourceProductDoMEqualsMaterial === "Post Manufacturing Documents"
                ? true
                : form.sourceProductDoMEqualsMaterial === "Inventory"
                ? false
                : null,
          },
          // updatePlantShipmentConfigsFlag: true,
          updateBatchConfiguration: true,
        };
      },
      validateForm: validate,
      resetForm: () => {
        setForm({ ...originalForm });
        setErrors({});
        setIsModified(false);
      },
    }));
    const handleChange = (e) => {
      const { name, value } = e.target;

      // For shipmentDuration field, only allow numbers
      if (name === "shipmentDuration") {
        // Only allow numbers, set isModified based on validity
        if (value === "" || /^[0-9]+$/.test(value)) {
          const processedValue = value === "" ? "" : value;
          setForm((prev) => ({ ...prev, [name]: processedValue }));
          setIsModified(true);

          if (onChange) {
            onChange({ target: { name, value: processedValue } });
          }
        } else {
          // Invalid input, don't update form but set isModified to false
          setIsModified(false);
        }
      } else {
        // For other fields, normal behavior
        setForm((prev) => ({ ...prev, [name]: value }));
        setIsModified(true);

        if (onChange) {
          onChange({ target: { name, value } });
        }
      }

      // Clear related errors
      if (errors[name]) {
        setErrors((prev) => ({ ...prev, [name]: "" }));
      }
    };
    const validate = () => {
      let valid = true;
      const newErrors = {};

      if (!form.allowMultipleBatches) {
        newErrors.allowMultipleBatches = "Required field";
        valid = false;
      }

      // Check if shipmentDuration is empty or not provided
      if (
        form.shipmentDuration === "" ||
        form.shipmentDuration === undefined ||
        form.shipmentDuration === null
      ) {
        newErrors.shipmentDuration = "Shipment Duration required";
        valid = false;
      } else if (
        isNaN(Number(form.shipmentDuration)) ||
        Number(form.shipmentDuration) <= 0
      ) {
        newErrors.shipmentDuration = "Must be a positive number";
        valid = false;
      }

      if (!form.automateGoodsReceiptProcess) {
        newErrors.automateGoodsReceiptProcess = "Required field";
        valid = false;
      }

      if (!form.allocationOwnedBySponsor) {
        newErrors.allocationOwnedBySponsor = "Required field";
        valid = false;
      }

      if (!form.batchAllocationPdfRequired) {
        newErrors.batchAllocationPdfRequired = "Required field";
        valid = false;
      }

      if (form.batchAllocationPdfRequired === "Yes") {
        if (!form.pdfFieldSubject || form.pdfFieldSubject.trim() === "") {
          newErrors.pdfFieldSubject = "This field is required.";
          valid = false;
        }
        if (!form.pdfFieldObjective || form.pdfFieldObjective.trim() === "") {
          newErrors.pdfFieldObjective = "This field is required.";
          valid = false;
        }
      }

      if (!form.sourceProductDoMEqualsMaterial) {
        newErrors.sourceProductDoMEqualsMaterial = "Required field";
        valid = false;
      }

      setErrors(newErrors);
      return valid;
    };

    return (
      <div className="cmo-stepper-wrapper shipment-config-step">
        <div className="stepper-container">
          <form>
            <Grid className="two-col-forms-grid" container spacing={3}>
              <Grid item xs={12} sm={6}>
                <FormControl style={{ width: "100%" }}>
                  <Typography component="label" htmlFor="allowMultipleBatches">
                    Allow Multiple Batches{" "}
                    {editMode && <span style={{ color: "red" }}>*</span>}
                  </Typography>
                  {!editMode ? (
                    <Typography>{form.allowMultipleBatches || ""}</Typography>
                  ) : (
                    <Select
                      select
                      id="allowMultipleBatches"
                      name="allowMultipleBatches"
                      value={form.allowMultipleBatches || ""}
                      onChange={handleChange}
                      variant="outlined"
                      fullWidth
                      required
                      error={!!errors.allowMultipleBatches}
                      helperText={errors.allowMultipleBatches || ""}
                      displayEmpty
                    >
                      <MenuItem value="" disabled>
                        Select from the list
                      </MenuItem>
                      {batchOptions.map((opt) => (
                        <MenuItem key={opt} value={opt}>
                          {opt}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl style={{ width: "100%" }}>
                  <Typography component="label" htmlFor="shipmentDuration">
                    Shipment Duration (Days){" "}
                    {editMode && <span style={{ color: "red" }}>*</span>}
                  </Typography>
                  {!editMode ? (
                    <Typography>{form.shipmentDuration || ""}</Typography>
                  ) : (
                    <TextField
                      id="shipmentDuration"
                      name="shipmentDuration"
                      value={
                        form.shipmentDuration !== undefined &&
                        form.shipmentDuration !== null
                          ? form.shipmentDuration
                          : ""
                      }
                      onChange={handleChange}
                      variant="outlined"
                      placeholder="Enter text here"
                      // type="number"
                      fullWidth
                      required
                      error={!!errors.shipmentDuration}
                      helperText={errors.shipmentDuration || ""}
                      InputProps={{ style: commonInlineStyles.input }}
                      sx={inputFieldStyles}
                      inputProps={{ ...commonInlineStyles.inputProps }}
                      style={commonInlineStyles.input}
                    />
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl style={{ width: "100%" }}>
                  <Typography
                    component="label"
                    htmlFor="automateGoodsReceiptProcess"
                  >
                    Automate goods receipt process{" "}
                    {editMode && <span style={{ color: "red" }}>*</span>}
                  </Typography>
                  {!editMode ? (
                    <Typography>
                      {form.automateGoodsReceiptProcess || ""}
                    </Typography>
                  ) : (
                    <Select
                      select
                      id="automateGoodsReceiptProcess"
                      name="automateGoodsReceiptProcess"
                      value={form.automateGoodsReceiptProcess || ""}
                      onChange={handleChange}
                      variant="outlined"
                      fullWidth
                      required
                      error={!!errors.automateGoodsReceiptProcess}
                      helperText={errors.automateGoodsReceiptProcess || ""}
                      displayEmpty
                    >
                      <MenuItem value="" disabled>
                        Select from the list
                      </MenuItem>
                      <MenuItem value="Yes">Yes</MenuItem>
                      <MenuItem value="No">No</MenuItem>
                    </Select>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl style={{ width: "100%" }}>
                  <Typography
                    component="label"
                    htmlFor="allocationOwnedBySponsor"
                  >
                    Allocation owned by Sponsor{" "}
                    {editMode && <span style={{ color: "red" }}>*</span>}
                  </Typography>
                  {!editMode ? (
                    <Typography>
                      {form?.allocationOwnedBySponsor || ""}
                    </Typography>
                  ) : (
                    <Select
                      select
                      id="allocationOwnedBySponsor"
                      name="allocationOwnedBySponsor"
                      value={form?.allocationOwnedBySponsor || ""}
                      onChange={handleChange}
                      variant="outlined"
                      fullWidth
                      required
                      error={!!errors?.allocationOwnedBySponsor}
                      helperText={errors?.allocationOwnedBySponsor || ""}
                      displayEmpty
                    >
                      <MenuItem value="" disabled>
                        Select from the list
                      </MenuItem>
                      <MenuItem value="Yes">Yes</MenuItem>
                      <MenuItem value="No">No</MenuItem>
                    </Select>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl style={{ width: "100%" }}>
                  <Typography
                    component="label"
                    htmlFor="batchAllocationPdfRequired"
                  >
                    Batch Allocation Notification PDF Required{" "}
                    {editMode && <span style={{ color: "red" }}>*</span>}
                  </Typography>
                  {!editMode ? (
                    <Typography>
                      {form.batchAllocationPdfRequired || ""}
                    </Typography>
                  ) : (
                    <Select
                      select
                      id="batchAllocationPdfRequired"
                      name="batchAllocationPdfRequired"
                      value={form.batchAllocationPdfRequired || ""}
                      onChange={handleChange}
                      variant="outlined"
                      fullWidth
                      required
                      error={!!errors.batchAllocationPdfRequired}
                      helperText={errors.batchAllocationPdfRequired || ""}
                      displayEmpty
                    >
                      <MenuItem value="" disabled>
                        Select from the list
                      </MenuItem>
                      <MenuItem value="Yes">Yes</MenuItem>
                      <MenuItem value="No">No</MenuItem>
                    </Select>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}></Grid>
              <Grid item xs={12} sm={6}>
                <FormControl style={{ width: "100%" }}>
                  <Typography component="label" htmlFor="sourceProductDoMEqualsMaterial">
                    Source for Product DoM = Material DoM cases{" "}
                    {editMode && <span style={{ color: "red" }}>*</span>}
                  </Typography>
                  {!editMode ? (
                    <Typography>
                      {form.sourceProductDoMEqualsMaterial || ""}
                    </Typography>
                  ) : (
                    <Select
                      id="sourceProductDoMEqualsMaterial"
                      name="sourceProductDoMEqualsMaterial"
                      value={form.sourceProductDoMEqualsMaterial || ""}
                      onChange={handleChange}
                      variant="outlined"
                      fullWidth
                      required
                      error={!!errors.sourceProductDoMEqualsMaterial}
                      displayEmpty
                    >
                      <MenuItem value="" disabled>
                        Select from the list
                      </MenuItem>
                      <MenuItem value="Post Manufacturing Documents">
                        Post Manufacturing Documents
                      </MenuItem>
                      <MenuItem value="Inventory">Inventory</MenuItem>
                    </Select>
                  )}
                  {errors.sourceProductDoMEqualsMaterial && (
                    <Typography style={{ color: "red", fontSize: 12 }}>
                      {errors.sourceProductDoMEqualsMaterial}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl style={{ width: "100%" }}>
                  <Typography component="label">
                    DoM present on Digitalization UI
                  </Typography>
                  <Typography>
                    {form.sourceProductDoMEqualsMaterial === "Post Manufacturing Documents"
                      ? "Yes"
                      : form.sourceProductDoMEqualsMaterial === "Inventory"
                      ? "No"
                      : "-"}
                  </Typography>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl style={{ width: "100%" }}>
                  <Typography component="label">
                    Source for Product DoM ≠ Material DoM cases
                  </Typography>
                  <Typography>
                    Post Manufacturing Documents
                    {/* {form.sourceProductDoMEqualsMaterial === "Post Manufacturing Documents"
                      ? "Inventory"
                      : form.sourceProductDoMEqualsMaterial === "Inventory"
                      ? "Post Manufacturing Documents"
                      : "-"} */}
                  </Typography>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl style={{ width: "100%" }}>
                  <Typography component="label">
                    DoM present on Digitalization UI
                  </Typography>
                  <Typography>
                    Yes
                    {/* {form.sourceProductDoMEqualsMaterial === "Post Manufacturing Documents"
                      ? "No"
                      : form.sourceProductDoMEqualsMaterial === "Inventory"
                      ? "Yes"
                      : "-"} */}
                  </Typography>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={12} style={{ maxWidth: "none" }}>
                <Typography
                  style={{
                    // fontSize: 12,
                    // color: "#666",
                    // fontStyle: "italic",
                    // marginTop: 0,
                    // width: 600,
                    maxWidth: "100%",
                    textAlign: "left",
                    marginTop: 2,
                  }}
                >
                  {`Note - `}
                  <br />
                  {`Sponsor can allocate batch for a PO, even when 'Allocation owned by Sponsor' configuration value is No.`}
                </Typography>
              </Grid>
            </Grid>
            <Divider
              style={{
                margin: "40px 0px 0px 0px",
                backgroundColor: "#707070",
                height: 1,
                zIndex: 4,
              }}
            />
            <Typography
              variant="h6"
              style={{ marginBottom: 32, fontWeight: 700, fontSize: 32 }}
            >
              {/* BCM-891: renamed from "Batch Allocation Notification PDF Fields". */}
              Batch Allocation Notification PDF Configurations
            </Typography>

            {/* BCM-891: Product Type Configurations, above To / CC / Subject /
                Objective, the same position and the same component as on the
                Add New CMO screen. */}

            <ProductTypePdfConfigTable
              rows={productTypeConfigs}
              disabled={!editMode}
              onChange={onProductTypeConfigChange}
              headClassName={classes.tableHead}
              headCellClassName={classes.tableHeadCell}
              bodyCellClassName={classes.tableBodyCell}
            />

            <Grid className="two-col-forms-grid" container spacing={3}>
              <Grid item xs={12} sm={6}>
                <FormControl style={{ width: "100%" }}>
                  <Typography component="label" htmlFor="pdfFieldTo">
                    To {editMode && <span style={{ color: "red" }}>*</span>}
                  </Typography>
                  {!editMode ? (
                    <Typography>{form.pdfFieldTo || ""}</Typography>
                  ) : (
                    <Select
                      select
                      id="pdfFieldTo"
                      name="pdfFieldTo"
                      value={form.pdfFieldTo || ""}
                      onChange={handleChange}
                      variant="outlined"
                      fullWidth
                      required
                      disabled={true}
                      error={!!errors.pdfFieldTo}
                      helperText={errors.pdfFieldTo || ""}
                      displayEmpty
                    >
                      <MenuItem value="" disabled>
                        Select from the list
                      </MenuItem>
                      <MenuItem value="CMO Name">CMO Name</MenuItem>
                    </Select>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl style={{ width: "100%" }}>
                  <Typography component="label" htmlFor="pdfFieldCC">
                    CC {editMode && <span style={{ color: "red" }}>*</span>}
                  </Typography>
                  {!editMode ? (
                    <Typography>{form.pdfFieldCC || ""}</Typography>
                  ) : (
                    <Select
                      select
                      id="pdfFieldCC"
                      name="pdfFieldCC"
                      value={form.pdfFieldCC || ""}
                      onChange={handleChange}
                      variant="outlined"
                      fullWidth
                      required
                      disabled={true}
                      error={!!errors.pdfFieldCC}
                      helperText={errors.pdfFieldCC || ""}
                      displayEmpty
                      sx={{
                        "& .MuiSelect-select": {
                          whiteSpace: "normal !important",
                          overflow: "visible !important",
                          textOverflow: "clip !important",
                          lineHeight: "1.5",
                          paddingTop: "8px",
                          paddingBottom: "8px",
                        },
                      }}
                      style={{
                        height: "auto",
                        minHeight: "40px",
                      }}
                      renderValue={(selected) => (
                        <div
                          style={{
                            whiteSpace: "normal",
                            wordWrap: "break-word",
                          }}
                        >
                          {selected || "Select from the list"}
                        </div>
                      )}
                      MenuProps={{
                        PaperProps: {
                          style: {
                            maxWidth: 600,
                          },
                        },
                      }}
                    >
                      <MenuItem value="" disabled>
                        Select from the list
                      </MenuItem>
                      <MenuItem
                        value="All the users associated with the CMO, Brand and Product Type"
                        style={{ whiteSpace: "normal", wordWrap: "break-word" }}
                      >
                        All the users associated with the CMO, Brand and Product
                        Type
                      </MenuItem>
                    </Select>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl
                  className={classes.noPaddingMultiline}
                  style={{ width: "100%" }}
                >
                  <Typography component="label" htmlFor="pdfFieldSubject">
                    Subject{" "}
                    {editMode && <span style={{ color: "red" }}>*</span>}
                  </Typography>
                  {!editMode ? (
                    <Typography>{form.pdfFieldSubject || ""}</Typography>
                  ) : (
                    <TextField
                      id="pdfFieldSubject"
                      name="pdfFieldSubject"
                      value={form.pdfFieldSubject || ""}
                      onChange={handleChange}
                      variant="outlined"
                      placeholder="Enter subject"
                      fullWidth
                      multiline
                      required
                      disabled={form.batchAllocationPdfRequired !== "Yes"}
                      sx={{
                        ...inputFieldStyles,
                        height: "100px !important",
                        "& .MuiOutlinedInput-root": {
                          padding: "0 !important",
                          height: "100px !important",
                          minHeight: "100px !important",
                          maxHeight: "100px !important",
                          alignItems: "flex-start !important",
                        },
                        "& .MuiInputBase-root": {
                          height: "100px !important",
                          minHeight: "100px !important",
                          maxHeight: "100px !important",
                        },
                        "& textarea": {
                          height: "100px !important",
                          minHeight: "100px !important",
                          maxHeight: "100px !important",
                          overflow: "auto !important",
                        },
                        "& .MuiOutlinedInput-input": {
                          padding: "8.5px 14px !important",
                          height: "100px !important",
                          minHeight: "100px !important",
                          maxHeight: "100px !important",
                          overflow: "auto !important",
                          boxSizing: "border-box !important",
                        },
                      }}
                      inputProps={{
                        maxLength: 255,
                        style: { height: "100px", overflow: "auto" },
                      }}
                      error={!!errors.pdfFieldSubject}
                      helperText={
                        errors.pdfFieldSubject
                          ? errors.pdfFieldSubject
                          : `${
                              form.pdfFieldSubject?.length || 0
                            }/255 characters`
                      }
                    />
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl
                  className={classes.noPaddingMultiline}
                  style={{ width: "100%" }}
                >
                  <Typography component="label" htmlFor="pdfFieldObjective">
                    Objective{" "}
                    {editMode && <span style={{ color: "red" }}>*</span>}
                  </Typography>
                  {!editMode ? (
                    <Typography>{form.pdfFieldObjective || ""}</Typography>
                  ) : (
                    <TextField
                      id="pdfFieldObjective"
                      name="pdfFieldObjective"
                      value={form.pdfFieldObjective || ""}
                      onChange={handleChange}
                      variant="outlined"
                      placeholder="Enter objective"
                      fullWidth
                      multiline
                      required
                      disabled={form.batchAllocationPdfRequired !== "Yes"}
                      sx={{
                        ...inputFieldStyles,
                        height: "175px !important",
                        "& .MuiOutlinedInput-root": {
                          padding: "0 !important",
                          height: "175px !important",
                          minHeight: "175px !important",
                          maxHeight: "175px !important",
                          alignItems: "flex-start !important",
                        },
                        "& .MuiInputBase-root": {
                          height: "175px !important",
                          minHeight: "175px !important",
                          maxHeight: "175px !important",
                        },
                        "& textarea": {
                          height: "175px !important",
                          minHeight: "175px !important",
                          maxHeight: "175px !important",
                          overflow: "auto !important",
                        },
                        "& .MuiOutlinedInput-input": {
                          padding: "8.5px 14px !important",
                          height: "175px !important",
                          minHeight: "175px !important",
                          maxHeight: "175px !important",
                          overflow: "auto !important",
                          boxSizing: "border-box !important",
                        },
                      }}
                      inputProps={{
                        maxLength: 500,
                        style: { height: "175px", overflow: "auto" },
                      }}
                      error={!!errors.pdfFieldObjective}
                      helperText={
                        errors.pdfFieldObjective
                          ? errors.pdfFieldObjective
                          : `${
                              form.pdfFieldObjective?.length || 0
                            }/500 characters`
                      }
                    />
                  )}
                </FormControl>
              </Grid>
            </Grid>
          </form>
          <Divider
            style={{
              margin: "80px 00px 0px 00px",
              backgroundColor: "#707070",
              height: 1,
            }}
          />
        </div>
      </div>
    );
  }
);

export default ShipmentConfigSection;
